import React, { useEffect, useState, useRef } from "react";
import "./QuotationPage.css";
import Logo from "../../../components/ui/Logo";
import { useParams, useNavigate } from "react-router-dom";
import { trackquotation } from "../../../services/trackQuotation";
import Loader from "../../../components/Loader/Loader";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import html2pdf from "html2pdf.js";
import { Helmet } from "react-helmet";

const currencyMap = {
  GBP: { name: "Pounds", symbol: "£" },
  EUR: { name: "Euro", symbol: "€" },
  USD: { name: "Dollars", symbol: "$" },
};

const QuotationPage = () => {
  const { quotationId } = useParams();
  const [quotationData, setQuotationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPaying, setIsPaying] = useState(false);
  const hasErrorToastShown = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        const data = await trackquotation(quotationId);
        setQuotationData(data);
        setLoading(false);
      } catch (err) {
        if (!hasErrorToastShown.current) {
          hasErrorToastShown.current = true;
          toast.error("Invalid Quotation ID");
          setTimeout(() => {
            navigate("/", { replace: true });
          }, 500);
        }
      }
    };

    if (quotationId) {
      fetchQuotation();
    }
  }, [quotationId, navigate]);

  const onPayNowClick = async () => {
    setIsPaying(true);
    try {
      const res = await axios.post(
        "https://api.shipperswise.com/create-payment",
        { quotation_id: quotationId },
        { accept: "application/json", contentType: "application/json" }
      );
      console.log(res);
      window.location.href = res?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setIsPaying(false);
    }
  };

  const handleDownload = () => {
    const element = document.querySelector(".quotation-container");
    const opt = {
      margin: 0.5,
      filename: `Quotation_${quotationData.shipment_id}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };
    html2pdf().set(opt).from(element).save();
  };

  if (loading) {
    return <Loader />;
  }

 const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`; // Adds comma after month
};



  return (
    <>
      <Helmet>
        <title>ShippersWise Quotation - {quotationId}</title>
      </Helmet>
      <ToastContainer />
      <div className="d-flex justify-content-center mt-5 no-print" style={{ gap: "20px", marginBottom: "-30px" }}>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleDownload();
          }}
        >
          Download
        </a>
        <span>|</span>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.print();
          }}
        >
          Print
        </a>
      </div>
      <div className="quotation-container">
        <header className="quotation-header" style={{ position: "relative" }}>
          <div>
            <Logo />
            <div className="quote-title">
              <div className="small-line"></div>
              <div className="big-line"></div>
              Quotation
            </div>
          </div>
          <div>
            <p className={`status-tag ${quotationData.quotation_status}`}>
              {quotationData.quotation_status.toUpperCase()}
            </p>
            {quotationData.quotation_status === "unpaid" && (
              <div onClick={onPayNowClick} className="pay-text no-print">
                <p>PAY NOW</p>
              </div>
            )}
          </div>
          <div className="quote-info">
            <p>Valid till: {formatDate(quotationData.quotation_valid_through_date)}</p>
            <p>
              Total: {currencyMap[quotationData.currency_type]?.symbol}
              {quotationData.quotation_sub_total}
            </p>
          </div>
        </header>

        <section>
          <div>
            <p className="bold-text">
              Quotation ID: #{quotationData.quotation_id}
            </p>
            <p className="bold-text">
              Shipment ID: #{quotationData.shipment_id}
            </p>
            <p>
              Date: <span style={{ fontWeight: "600" }}>{formatDate (quotationData.quotation_valid_through_date)}</span>
            </p>
          </div>
        </section>

        <div className="line-break"></div>

        <div className="quote-details">
          <div className="quote-from">
            <p className="bold-text">From</p>
            <p>ShippersWise</p>
            <p>97 Hatherley Court Hatherley Grove,</p>
            <p>London, England, W2 5RF</p>
          </div>
          <div className="quote-to">
            <p className="bold-text">To:</p>
            <p>{quotationData.user_name}</p>
            <p>{quotationData.user_email}</p>
            <p>
              {quotationData.user_postal_code}, {quotationData.user_city},{" "}
              {quotationData.user_country}
            </p>
            <p>{quotationData.user_phone}</p>
          </div>
        </div>

        <div className="line-break two"></div>

        <div className="d-flex justify-content-between align-items-center table-title">
          <p>Item</p>
          <p className="table-rate">Rate</p>
          <p>Quantity</p>
          <p>Total</p>
        </div>

        <div className="line-break two"></div>

        {quotationData.quotation_items.map((item, index) => (
          <div key={index}>
            <div className="d-flex justify-content-between table-body">
              <div>
                <p
                  className="bold-text"
                  style={{ fontWeight: "500", color: "#000" }}
                >
                  {item.item_name}
                </p>
                <p>{item.description}</p>
              </div>
              <p>
                {currencyMap[quotationData.currency_type]?.symbol}
                {item.rate}
              </p>
              <p>{item.quantity}</p>
              <p>
                {currencyMap[quotationData.currency_type]?.symbol}
                {item.total}
              </p>
            </div>
            <div className="black"></div>
          </div>
        ))}

        <div className="d-flex justify-content-between" style={{ padding: "0px 20px" }}>
          <div className="instructions">
            <p className="bold-text" style={{ marginTop: "20px" }}>
              Note:
            </p>
            <p>{quotationData.quotation_notes}</p>
          </div>
          <div className="totals">
            <p>
              Subtotal: {currencyMap[quotationData.currency_type]?.symbol}
              {quotationData.quotation_sub_total}
            </p>
            <p>
              Discount: {currencyMap[quotationData.currency_type]?.symbol}
              {quotationData.quotation_discount_amount}
            </p>
            <p>
              Tax: {currencyMap[quotationData.currency_type]?.symbol}
              {quotationData.quotation_tax_percent}
            </p>
          </div>
        </div>

        <div className="line-break"></div>

        <section className="total-wrapper">
          <div className="total-balance">
            <div className="d-flex justify-content-center align-items-center balance-title">
              <p>Total </p>
              <p>
                {currencyMap[quotationData.currency_type]?.symbol}
                {quotationData.total}
              </p>
            </div>
            {quotationData.quotation_status === "unpaid" && (
              <div className="now-btn no-print" onClick={onPayNowClick}>
                {isPaying ? "Loading..." : <p>Pay Now</p>}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default QuotationPage;